import { useCallback, useMemo } from 'react';

import { ICombo, IModifierSelection, IPicker } from '@rbi-ctg/menu';
import { FIRST_INDEX } from 'components/product-detail-v2/product-detail-flow/product-picker-flow/constants';
import { findCheapestPickerOption } from 'components/product-detail-v2/product-detail-flow/product-picker-flow/utils';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail-v2/utils';
import { useRedeemReward } from 'pages/cart/cart-item/hooks/use-redeem-reward-state';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { useUIContext } from 'state/ui';
import { isPicker } from 'utils/menu';
import { IWithVendorConfig } from 'utils/vendor-config';

import { computeDiscountPrice, getOfferVendorConfigForPricing } from './utils';

/**
 * Helper to get the amount of items to consider when computing the price
 */
const getBaseQuantityForPricing = ({
  shouldIncludeQuantity,
  productQuantity,
  isReward,
  enableRewardUnstacking,
}: {
  shouldIncludeQuantity: boolean;
  productQuantity: number;
  isReward: boolean;
  enableRewardUnstacking: boolean;
}) => {
  if (shouldIncludeQuantity) {
    return productQuantity;
  }

  /**
   * If pricing a reward and reward unstacking is enabled, we want to show the actual item price
   * if adding more than one reward. That's why, when `shouldIncludeQuantity` is false, we want
   * to consider at most two items as that will get the actual price for the item ($0 for the first
   * item and the full price for the second item)
   */
  if (isReward && enableRewardUnstacking) {
    return Math.min(productQuantity, 2);
  }

  return 1;
};

export interface ProductPriceConditions {
  shouldIncludeModifications: boolean;
  shouldIncludeQuantity: boolean;
}

export const useProductPrice = (conditions?: ProductPriceConditions) => {
  const { isOffer, pricingFunction } = useMenuContext();
  const {
    menuObject,
    productQuantity,
    selectedProduct,
    userSelections,
  } = useProductWizardContext();
  const { formatCurrencyForLocale } = useUIContext();

  const { incentiveNotInMenu } = useRedeemReward();
  const { rewardBenefitId, cartIdEditing, isFromCart, currentStep } = useProductWizardFlow();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);

  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);

  const selectedOffer = selectedLoyaltyOffer;

  const { shouldIncludeModifications = false, shouldIncludeQuantity = false } = conditions || {};

  const discountPrice = useCallback(() => {
    return computeDiscountPrice({
      appliedLoyaltyRewards,
      availableLoyaltyRewardsMap,
      cartIdEditing,
      pricingFunction,
      productQuantity,
      rewardBenefitId,
      selectedProduct,
      menuObject,
      stagedCartPoints,
      isFromCart,
      preventRewardStacking: enableRewardUnstacking,
    });
  }, [
    appliedLoyaltyRewards,
    availableLoyaltyRewardsMap,
    cartIdEditing,
    pricingFunction,
    productQuantity,
    rewardBenefitId,
    selectedProduct,
    menuObject,
    stagedCartPoints,
    isFromCart,
    enableRewardUnstacking,
  ]);

  const minPriceForPicker = useCallback(() => {
    const pickerData = menuObject as IPicker;
    const firstPickerAspect = pickerData.pickerAspects[FIRST_INDEX];
    const userSelectionsForFirstPickerAspect =
      userSelections.pickerAspects?.[firstPickerAspect._id];

    const quantity = shouldIncludeQuantity ? productQuantity : 1;

    const price = findCheapestPickerOption({
      pickerData,
      pickerAspectOptionIdentifier: userSelectionsForFirstPickerAspect || '',
      pricingFunction,
    });

    // If it's a reward, we should account for disccount price
    const priceWithDiscount = Math.max(price * quantity - discountPrice(), 0);
    return priceWithDiscount;
  }, [
    discountPrice,
    menuObject,
    pricingFunction,
    productQuantity,
    shouldIncludeQuantity,
    userSelections.pickerAspects,
  ]);

  const totalPrice = useCallback(
    (modifiersInjected?: IModifierSelection[]) => {
      const quantity = getBaseQuantityForPricing({
        shouldIncludeQuantity,
        productQuantity,
        isReward: !!rewardBenefitId,
        enableRewardUnstacking,
      });

      const baseTotal = pricingFunction({ item: selectedProduct, quantity });

      let total = baseTotal;
      if (isOffer) {
        const vendorConfigs = getOfferVendorConfigForPricing(selectedOffer);
        const price = vendorConfigs
          ? pricingFunction({
              // TODO: add _type
              item: { vendorConfigs } as IWithVendorConfig,
              quantity,
              modifierSelections: modifiersInjected,
            })
          : 0;
        total = price || selectedOffer?.offerPrice || 0;
      }

      if (shouldIncludeModifications) {
        const comboSlotSelections = transformUserSelectionsComboToComboSlotSelections(
          userSelections.comboSlot,
          selectedProduct as ICombo
        );
        const modifierSelections = transformUserSelectionsModifierToModifierSelection(
          userSelections
        );
        const extraTotal =
          pricingFunction({
            item: selectedProduct,
            quantity,
            modifierSelections,
            comboSlotSelections,
          }) - baseTotal;

        total += extraTotal;
      }
      return total;
    },
    [
      enableRewardUnstacking,
      isOffer,
      pricingFunction,
      productQuantity,
      rewardBenefitId,
      selectedOffer,
      selectedProduct,
      shouldIncludeModifications,
      shouldIncludeQuantity,
      userSelections,
    ]
  );

  const formattedProductPrice = useMemo(() => {
    // On the Product Home page, we want to show the cheapest price for the picker.
    if (isPicker(menuObject) && currentStep === ProductWizardStep.ProductHome) {
      if (isOffer && selectedOffer) {
        return formatCurrencyForLocale(selectedOffer.offerPrice || 0);
      }

      const priceForPicker = minPriceForPicker();
      return formatCurrencyForLocale(priceForPicker);
    }

    const totalWithDiscount = incentiveNotInMenu ? 0 : Math.max(totalPrice() - discountPrice(), 0);
    return formatCurrencyForLocale(totalWithDiscount);
  }, [
    menuObject,
    currentStep,
    incentiveNotInMenu,
    totalPrice,
    discountPrice,
    formatCurrencyForLocale,
    isOffer,
    selectedOffer,
    minPriceForPicker,
  ]);

  return {
    totalPrice,
    formattedProductPrice,
  };
};
